import { useState } from "react";
import { CaretDown, Warning, ArrowsOutSimple } from "@phosphor-icons/react";
import { ClassificationBadge, Confidence } from "@/components/ClassificationBadge";
import EvidencePanel from "@/components/EvidencePanel";

const arr = (x) => (Array.isArray(x) ? x : x ? [x] : []);

export const MAP_SECTIONS = [
  { id: "formulation", n: "01", title: "Current Challenge Formulation" },
  { id: "aim", n: "02", title: "Current Developmental Aim" },
  { id: "well", n: "03", title: "What Appears Well Represented" },
  { id: "missing", n: "04", title: "What Appears Missing or Underrepresented" },
  { id: "claims", n: "05", title: "Claims & Current Evidentiary Status" },
  { id: "stakeholders", n: "06", title: "Stakeholders, Needs, Goals, Constraints, Resources, Consequences" },
  { id: "boundaries", n: "07", title: "System Boundaries & Time Horizons" },
  { id: "causal", n: "08", title: "Causal Reasoning Requiring Examination" },
  { id: "blindspots", n: "09", title: "Possible Blind Spots or Biases" },
  { id: "framings", n: "10", title: "Alternative Framings" },
  { id: "evidence", n: "11", title: "Evidence" },
  { id: "evidence-needs", n: "12", title: "Unresolved Evidence Needs" },
  { id: "uncertainties", n: "13", title: "Remaining Uncertainties" },
];

const Section = ({ id, n, title, count, children }) => (
  <section id={id} data-testid={`section-${id}`} className="scroll-mt-24 border-t border-border py-10">
    <div className="mb-5 flex items-baseline gap-4">
      <span className="font-mono text-xs text-muted-foreground">{n}</span>
      <h2 className="flex-1 font-serif text-2xl leading-tight sm:text-3xl">{title}</h2>
      {typeof count === "number" && (
        <span className="font-mono text-xs text-muted-foreground">{count}</span>
      )}
    </div>
    {children}
  </section>
);

const Expandable = ({ summary, meta, children, testid, tone }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="border border-border bg-card transition-colors hover:border-foreground/30"
      style={tone ? { borderLeft: `3px solid ${tone}` } : undefined}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
      >
        <div className="flex-1">{summary}</div>
        <div className="flex items-center gap-3 pt-0.5">
          {meta}
          <CaretDown
            size={16}
            className="shrink-0 text-muted-foreground transition-transform duration-200"
            style={{ transform: open ? "rotate(180deg)" : "none" }}
          />
        </div>
      </button>
      {open && <div className="reveal border-t border-border px-5 py-4">{children}</div>}
    </div>
  );
};

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="mb-3 last:mb-0">
      <div className="overline mb-1">{label}</div>
      <p className="text-sm leading-relaxed text-muted-foreground">{value}</p>
    </div>
  );
};

const ConcernCard = ({ c, testid, tone }) => (
  <Expandable
    testid={testid}
    tone={tone}
    summary={<p className="text-[15px] leading-relaxed">{c.concern}</p>}
    meta={c.confidence ? <Confidence level={c.confidence} /> : null}
  >
    <DetailRow label="Basis in the CEO's account" value={c.basis_in_ceo_account} />
    <DetailRow label="Significance" value={c.significance} />
    <DetailRow label="Current evidentiary basis" value={c.current_evidentiary_basis} />
    <DetailRow label="Evidence that would help" value={c.evidence_that_would_help} />
    <DetailRow label="Evidence that would help" value={c.evidence_that_would_help} />
  </Expandable>
);

const ClaimCard = ({ c, testid }) => (
  <Expandable
    testid={testid}
    summary={
      <div>
        <p className="mb-2 text-[15px] leading-relaxed">"{c.exact_or_close_paraphrase}"</p>
        <div className="flex flex-wrap items-center gap-2">
          <ClassificationBadge classification={c.classification} />
          {c.current_evidentiary_status && (
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              · {c.current_evidentiary_status}
            </span>
          )}
        </div>
      </div>
    }
    meta={c.confidence ? <Confidence level={c.confidence} /> : null}
  >
    <DetailRow label="Why classified this way" value={c.explanation} />
    <DetailRow label="Current evidentiary basis" value={c.current_evidentiary_basis} />
    <DetailRow label="Evidence currently available" value={c.evidence_currently_available} />
    <DetailRow label="Evidence referenced" value={c.evidence_reference} />
    <DetailRow label="Evidence that would strengthen" value={c.evidence_that_would_strengthen} />
    <DetailRow label="Evidence that would weaken or challenge" value={c.evidence_that_would_weaken} />
  </Expandable>
);

const Prose = ({ children }) => (
  <p className="max-w-[68ch] text-[17px] leading-relaxed text-foreground/90">{children}</p>
);

const Chips = ({ title, items }) => {
  const list = arr(items).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="mb-6">
      <div className="overline mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {list.map((it, i) => (
          <span key={i} className="rounded-sm border border-border bg-muted px-3 py-1.5 text-sm">
            {typeof it === "string" ? it : JSON.stringify(it)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function ChallengeMap({ map, ceoEvidence = [] }) {
  if (!map) return null;
  const sys = map.stakeholders_and_system || {};

  return (
    <div className="max-w-3xl">
      {/* 01 */}
      <Section {...MAP_SECTIONS[0]}>
        <Prose>{map.current_challenge_formulation}</Prose>
      </Section>

      {/* 02 */}
      <Section {...MAP_SECTIONS[1]}>
        <Prose>{map.current_developmental_aim}</Prose>
      </Section>

      {/* 03 */}
      <Section {...MAP_SECTIONS[2]} count={arr(map.well_represented).length}>
        <div className="space-y-3">
          {arr(map.well_represented).map((w, i) => (
            <Expandable
              key={i}
              testid={`well-item-${i}`}
              summary={<p className="text-[15px] leading-relaxed">{w.point || w}</p>}
            >
              <DetailRow label="Basis" value={w.basis} />
            </Expandable>
          ))}
        </div>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-well" />
      </Section>

      {/* 04 */}
      <Section {...MAP_SECTIONS[3]} count={arr(map.missing_or_underrepresented).length}>
        <div className="space-y-3">
          {arr(map.missing_or_underrepresented).map((c, i) => (
            <ConcernCard key={i} c={c} testid={`missing-item-${i}`} tone="hsl(var(--tag-assumption))" />
          ))}
        </div>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-missing" />
      </Section>

      {/* 05 */}
      <Section {...MAP_SECTIONS[4]} count={arr(map.claims).length}>
        <div className="space-y-3">
          {arr(map.claims).map((c, i) => (
            <ClaimCard key={i} c={c} testid={`claim-item-${i}`} />
          ))}
        </div>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-claims" />
      </Section>

      {/* 06 */}
      <Section {...MAP_SECTIONS[5]}>
        {arr(sys.stakeholders).length > 0 && (
          <div className="mb-6">
            <div className="overline mb-2">Stakeholders</div>
            <div className="divide-y divide-border border border-border">
              {arr(sys.stakeholders).map((s, i) => (
                <div key={i} className="flex items-start justify-between gap-4 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    {s.note && <p className="mt-0.5 text-sm text-muted-foreground">{s.note}</p>}
                  </div>
                  <span
                    className="mt-0.5 shrink-0 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]"
                    style={{
                      color: s.represented ? "hsl(var(--tag-preference))" : "hsl(var(--tag-hearsay))",
                      borderColor: s.represented ? "hsl(var(--tag-preference))" : "hsl(var(--tag-hearsay))",
                    }}
                  >
                    {s.represented ? "represented" : "absent / silent"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Chips title="Needs" items={sys.needs} />
        <Chips title="Goals" items={sys.goals} />
        <Chips title="Constraints" items={sys.constraints} />
        <Chips title="Resources" items={sys.resources} />
        <Chips title="Consequences" items={sys.consequences} />
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-stakeholders" />
      </Section>

      {/* 07 */}
      <Section {...MAP_SECTIONS[6]}>
        <DetailRow label="System boundaries" value={map.system_boundaries} />
        <div className="mt-4" />
        <DetailRow label="Time horizons" value={map.time_horizons} />
      </Section>

      {/* 08 */}
      <Section {...MAP_SECTIONS[7]} count={arr(map.causal_reasoning).length}>
        <div className="space-y-3">
          {arr(map.causal_reasoning).map((c, i) => (
            <Expandable
              key={i}
              testid={`causal-item-${i}`}
              tone="hsl(var(--tag-causal))"
              summary={<p className="text-[15px] leading-relaxed">{c.claim}</p>}
              meta={c.confidence ? <Confidence level={c.confidence} /> : null}
            >
              <DetailRow label="Why it requires examination" value={c.concern} />
              <DetailRow label="Basis in the CEO's account" value={c.basis_in_ceo_account} />
              <DetailRow label="Evidence needed" value={c.evidence_needed} />
            </Expandable>
          ))}
        </div>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-causal" />
      </Section>

      {/* 09 */}
      <Section {...MAP_SECTIONS[8]} count={arr(map.blind_spots_biases).length}>
        <p className="mb-4 max-w-[68ch] text-sm italic text-muted-foreground">
          Each concern states its basis in your own account. None is an accusation.
        </p>
        <div className="space-y-3">
          {arr(map.blind_spots_biases).map((c, i) => (
            <ConcernCard key={i} c={c} testid={`blindspot-item-${i}`} tone="hsl(var(--tag-hearsay))" />
          ))}
        </div>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} testid="evidence-panel-blindspots" />
      </Section>

      {/* 10 */}
      <Section {...MAP_SECTIONS[9]} count={arr(map.alternative_framings).length}>
        <div className="grid gap-3">
          {arr(map.alternative_framings).map((f, i) => (
            <Expandable
              key={i}
              testid={`framing-item-${i}`}
              summary={
                <div className="flex items-start gap-3">
                  <ArrowsOutSimple size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <div>
                    {f.label && <div className="overline mb-1">{f.label}</div>}
                    <p className="text-[15px] leading-relaxed">{f.framing}</p>
                  </div>
                </div>
              }
            >
              <DetailRow label="How it differs" value={f.how_it_differs} />
              <DetailRow label="What it reveals" value={f.what_it_reveals} />
            </Expandable>
          ))}
        </div>
      </Section>

      {/* 11 */}
      <Section {...MAP_SECTIONS[10]}>
        <EvidencePanel evidence={map.evidence} ceoSupplied={ceoEvidence} compact={false} testid="evidence-panel-main" />
        <div className="mt-4 border-t border-border pt-4 text-sm italic text-muted-foreground">
          No external evidence has been consulted. Only evidence you supply appears here.
        </div>
      </Section>

      {/* 12 */}
      <Section {...MAP_SECTIONS[11]} count={arr(map.unresolved_evidence_needs).length}>
        <div className="space-y-3">
          {arr(map.unresolved_evidence_needs).map((n, i) => (
            <Expandable
              key={i}
              testid={`evidence-need-${i}`}
              summary={<p className="text-[15px] leading-relaxed">{n.need || n}</p>}
            >
              <DetailRow label="Why it matters" value={n.why_it_matters} />
            </Expandable>
          ))}
        </div>
      </Section>

      {/* 13 */}
      <Section {...MAP_SECTIONS[12]} count={arr(map.remaining_uncertainties).length}>
        <div className="space-y-3">
          {arr(map.remaining_uncertainties).map((u, i) => (
            <div key={i} data-testid={`uncertainty-item-${i}`} className="flex items-start gap-3 border border-dashed border-border px-4 py-3">
              <Warning size={16} className="mt-0.5 shrink-0" style={{ color: "hsl(var(--tag-uncertainty))" }} />
              <div>
                <p className="text-[15px] leading-relaxed">{u.uncertainty || u}</p>
                {u.significance && (
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{u.significance}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
